import { Input, Form, Button, message, Tag, Tooltip } from "antd";

import { restrictInputToNumbers } from "utils/inpututils";
import { Dispatch, SetStateAction, useMemo } from "react";
import { getDaysText } from "utils/utils";
import { DateUtils } from "utils/dateutils";
import { CheckCircleOutlined } from "@ant-design/icons";
import {
  EstimateStatus,
  IMaterial,
  UpdateLeadTimeRequestData
} from "./type-definitions";
import { updateLeadTimeRequestDetails } from "./services";

function MaterialDurationsSection({
  material,
  token,
  submittingState,
  onSubmitSuccess
}: {
  material: IMaterial;
  token: string;
  submittingState: [boolean, Dispatch<SetStateAction<boolean>>];
  onSubmitSuccess: () => void;
}) {
  const [submitting, setSubmitting] = submittingState;

  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);

  const initialValues = useMemo(() => {
    return material.offsets?.reduce((acc: any, offset) => {
      acc[offset.id] = offset.value;
      return acc;
    }, {});
  }, [material.offsets]);

  const durationsTotal = useMemo(() => {
    return Object.values(formValues || {}).reduce<number>(
      (acc: number, value: any) => acc + Number(value),
      0
    );
  }, [formValues]);

  const isSubmittedOnce = useMemo(() => {
    return material.estimate_status === EstimateStatus.SUBMITTED;
  }, [material.estimate_status]);

  const onSubmit = async (values: any) => {
    setSubmitting(true);
    // console.log(values);

    const data: UpdateLeadTimeRequestData = [
      {
        estimate_invite_feature_id: material.estimate_invite_feature_id,
        feature_instance_id: material.id,
        offsets: material.offsets?.map((offset) => ({
          id: offset.id,
          value: Number(values[offset.id])
        }))
      }
    ];

    if (!token) {
      console.log("Update request token not found");
      message.error("Failed to update lead time");
      return;
    }
    const response = await updateLeadTimeRequestDetails(token, data);
    // console.log("response ", response);

    if (response.success) {
      message.success("Submitted successfully");
      onSubmitSuccess();
    } else {
      message.error("Failed to update lead time");
    }
    setSubmitting(false);
  };

  if (!material.offsets) return null;

  return (
    <div className="p-7 md:p-5 !pt-0" data-testid="material-durations-section">
      <div
        className="text-sm font-semibold text-color-1 lg:sticky lg:top-0 bg-white py-3 md:py-5 z-10"
        data-testid="material-durations-header"
      >
        Enter Lead Times
      </div>
      <div className="w-full xl:w-3/5">
        <Form
          layout="vertical"
          form={form}
          onFinish={onSubmit}
          initialValues={initialValues}
        >
          {material?.offsets
            ?.sort((a, b) => a.order - b.order)
            .map((offset) => (
              <Tooltip
                placement="right"
                title={
                  !offset.is_editable
                    ? "Lead Time cannot be edited for completed steps. "
                    : ""
                }
              >
                <Form.Item
                  label={
                    !offset.is_editable ? (
                      <div className="flex justify-between items-end w-full">
                        <div>{offset.name}</div>
                        {!offset.is_editable && (
                          <div className="-mr-2 py-[2px]">
                            <Tag
                              className="rounded-[4px] text-ciq-success-text font-normal py-[2px]"
                              icon={
                                <CheckCircleOutlined
                                  style={{ color: "#52C41A" }}
                                />
                              }
                              color="green"
                            >
                              Completed
                            </Tag>
                          </div>
                        )}
                      </div>
                    ) : (
                      offset.name
                    )
                  }
                  name={offset.id}
                  rules={[
                    {
                      required: offset.is_editable,
                      message: "Please enter lead time in numbers only."
                    }
                  ]}
                  key={offset.id}
                  data-testid={`material-duration-item-${offset.id}`}
                >
                  <Input
                    type="number"
                    suffix="Days"
                    placeholder="0"
                    min={0}
                    onKeyDown={restrictInputToNumbers}
                    tabIndex={0}
                    data-testid={`material-duration-input-${offset.id}`}
                    disabled={!offset.is_editable}
                  />
                </Form.Item>
              </Tooltip>
            ))}
          <div
            className="mt-4 flex items-center justify-between text-sm font-semibold "
            data-testid="material-durations-total-row"
          >
            <div
              className="text-color-1"
              data-testid="material-durations-total-label"
            >
              Total
            </div>
            <div
              className="text-color-3"
              data-testid="material-durations-total-value"
            >
              {durationsTotal} {getDaysText(durationsTotal)}
            </div>
          </div>
          <div className="flex justify-end mt-2.5">
            <Button
              type={isSubmittedOnce ? "default" : "primary"}
              htmlType="submit"
              className="w-full"
              loading={submitting}
              data-testid="material-durations-submit-btn"
            >
              {isSubmittedOnce ? "Submit Again" : "Submit"}
            </Button>
          </div>
          {material.updated_at ? (
            <div
              className="mt-2.5 text-xs text-color-1 text-center italic"
              data-testid="material-durations-last-submitted"
            >
              Last submitted on:{" "}
              {DateUtils.format(material.updated_at, "MM-DD-YYYY")} at{" "}
              {DateUtils.format(material.updated_at, "hh:mm A (z)")}
            </div>
          ) : null}
        </Form>
      </div>
    </div>
  );
}

export default MaterialDurationsSection;
